import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './HelpTip.module.css';

const TIP_WIDTH = 280;
const GAP = 8;

/**
 * Small (?) badge that shows a help bubble on hover (desktop) or tap (iPad).
 *
 * Renders as a <span role="button"> rather than a <button> so it can sit inside
 * clickable headers (group toggles) without nesting interactive elements; clicks
 * stop propagation so tapping the badge never toggles/administers the parent.
 * The bubble is portalled to <body> with fixed positioning so overflow:hidden
 * cards can't clip it.
 */
export default function HelpTip({ children, label = 'Help' }) {
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false); // opened by tap/click — stays until dismissed
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    if (!a) return;
    const vw = window.innerWidth;
    const width = Math.min(TIP_WIDTH, vw - 16);
    const left = Math.max(8, Math.min(a.left + a.width / 2 - width / 2, vw - width - 8));
    const h = bubbleRef.current?.offsetHeight ?? 0;
    // Prefer below; flip above when it would run off the bottom.
    const below = a.bottom + GAP;
    const top = h && below + h > window.innerHeight - 8 && a.top - GAP - h > 8
      ? a.top - GAP - h
      : below;
    setPos({ left, top, width });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (anchorRef.current?.contains(e.target) || bubbleRef.current?.contains(e.target)) return;
      setOpen(false); setPinned(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setPinned(false); } };
    const onScroll = () => place();
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (pinned) { setPinned(false); setOpen(false); } else { setPinned(true); setOpen(true); }
  };

  return (
    <>
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        className={`${styles.badge} ${open ? styles.badgeOpen : ''}`}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse' && !pinned) setOpen(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!pinned) setOpen(false); }}
      >
        ?
      </span>
      {open && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          className={styles.bubble}
          style={pos
            ? { left: pos.left, top: pos.top, width: pos.width }
            : { left: -9999, top: 0, width: TIP_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
